import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Building2, BarChart3, Users, Shield, DollarSign, Settings } from 'lucide-react'
import ParishApplicationModal from '../../components/ParishApplicationModal'
import { usePageMeta } from '@/hooks/usePageMeta'

export default function ForParishes() {
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  usePageMeta({
    title: 'For Parishes | Claritas',
    description:
      'Strengthen your parish community\'s generosity. Accept secure round-up and one-time donations with real-time tracking, donor reporting, and zero subscription fees.',
  })

  return (
    <div className="min-h-screen bg-background-light">

      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            For Parishes
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Strengthen Your Community's Generosity
          </h1>
          <p className="text-base text-charcoal-muted max-w-2xl mx-auto mb-8">
            Claritas gives your parish a modern, secure giving platform that connects you directly to your
            community. Accept round-up donations and one-time gifts with full transparency and zero complexity.
          </p>
          <Button size="lg" onClick={() => setModalOpen(true)} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
            Register Your Parish
          </Button>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-background-light py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Why Claritas
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Built for Your Parish
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              A platform designed with the needs of Catholic communities in mind.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <DollarSign className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Consistent Giving</h3>
              <p className="text-sm text-charcoal-muted">
                Round-up donations create a steady, predictable stream of contributions from your
                community—supplementing collections without replacing them.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <BarChart3 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Real-Time Dashboard</h3>
              <p className="text-sm text-charcoal-muted">
                Monitor all incoming donations in real time. View totals by donor, date, and type.
                Export reports for bookkeeping and financial planning.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Shield className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Secure Payments</h3>
              <p className="text-sm text-charcoal-muted">
                All transactions are processed through Stripe, ensuring donations are handled securely
                and deposited directly into your parish's account.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Users className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Donor Visibility</h3>
              <p className="text-sm text-charcoal-muted">
                See which members of your community are contributing. Understand giving patterns and
                engage with your most active supporters.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Building2 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Parish Directory</h3>
              <p className="text-sm text-charcoal-muted">
                Your parish is listed in the Claritas directory, making it easy for existing and
                prospective parishioners to find and support your community.
              </p>
            </div>

            <div className="bg-white p-6 border border-surface-border rounded shadow-card hover:border-gold/30 hover:shadow-subtle transition-all duration-150">
              <div className="w-10 h-10 border border-gold/30 rounded flex items-center justify-center mb-5">
                <Settings className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Simple Administration</h3>
              <p className="text-sm text-charcoal-muted">
                Manage your parish profile, view donor activity, and configure your account from one
                intuitive admin dashboard—no technical expertise required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding Steps */}
      <section className="bg-white py-20 border-t border-surface-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
              Onboarding
            </p>
            <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
              Up and Running in Minutes
            </h2>
            <p className="text-base text-charcoal-muted max-w-xl mx-auto">
              Getting your parish on Claritas is straightforward. No lengthy contracts or complicated setup.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                1
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Register Your Parish</h3>
              <p className="text-sm text-charcoal-muted">
                Create an admin account and add your parish name, address, and community details
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                2
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Connect Stripe</h3>
              <p className="text-sm text-charcoal-muted">
                Link your banking details through Stripe to receive donations directly and securely
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 border-2 border-gold text-gold flex items-center justify-center text-xl font-bold font-display mx-auto mb-5">
                3
              </div>
              <h3 className="text-lg font-bold font-display text-charcoal mb-2">Invite Your Community</h3>
              <p className="text-sm text-charcoal-muted">
                Share Claritas with your parishioners and start receiving automated and one-time gifts
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-light py-20 border-t border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Get Started
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h2 className="text-3xl font-bold font-display text-charcoal mb-4">
            Bring Claritas to Your Parish
          </h2>
          <p className="text-base text-charcoal-muted mb-8 max-w-xl mx-auto">
            Join the growing network of Catholic parishes using Claritas to deepen community engagement
            and support their mission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" onClick={() => setModalOpen(true)} className="bg-gold text-black font-semibold hover:bg-gold/90 shadow-gold">
              Register Your Parish
            </Button>
            <Button variant="ghost" size="lg" onClick={() => navigate('/pricing')}>
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <ParishApplicationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  )
}
